import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";
import api from "services/api";

/**
 * Only require limited informations. the style information is a minimal set for creating a project
 */
const EditRecordModal = React.forwardRef(
  ({ visible, handleClose, handleOk, style = {}, editType = "all" }, ref) => {
    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      reset,
      control,
      formState: { errors },
    } = useForm();

    useEffect(() => {
      const styleMeta = style?.style ? style.style : {};
      reset({
        quantity: styleMeta?.quantity || 0,
        cost_per_unit: styleMeta?.cost_per_unit || 0,
      });
    }, []);

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 0 }}>
          {"Project Meta"}
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit(handleOk)} style={{ overflow: "scroll", maxHeight: "600px" }}>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" sx={{ mb: 1 }}>
              Please set quantity and cost to "0" if you only need develop services from
              manufacturer.
            </DialogContentText>
            <Grid container spacing={2}>
              {editType == "quantity" || editType == "all" ? (
                <Grid item xs={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                        required
                      >
                        Quantity
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      placeholder="Quantity"
                      type="number"
                      componentsProps={{ input: { min: 0 } }}
                      required={true}
                      {...register("quantity", {
                        min: {
                          value: 0,
                          message: "Mininal amount should be greater or equal than 0!",
                        },
                      })}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="quantity"
                      render={({ message }) => (
                        <SuiTypography
                          variant="text"
                          fontWeight="regular"
                          fontSize="12px"
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          {message}
                        </SuiTypography>
                      )}
                    />
                  </SuiBox>
                </Grid>
              ) : null}
              {editType == "cost_per_unit" || editType == "all" ? (
                <Grid item xs={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                        required
                      >
                        Target Unit Cost
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      icon={{
                        direction: "left",
                        component: <AttachMoneyIcon />,
                      }}
                      placeholder="Cost"
                      type="number"
                      componentsProps={{ input: { min: 0 } }}
                      inputProps={{ step: 0.01 }}
                      required={true}
                      {...register("cost_per_unit", {
                        min: {
                          value: 0,
                          message: "Mininal costs should be greater or equal than 0!",
                        },
                      })}
                    />
                  </SuiBox>
                  <ErrorMessage
                    errors={errors}
                    name="cost_per_unit"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                </Grid>
              ) : null}
            </Grid>
            <SuiBox
              pt={3}
              px={3}
              display="flex"
              justifyContent="end"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                    Cancel
                  </SuiButton>
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                    Save
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
);

export default EditRecordModal;
